import create from 'zustand'
import { arrayToObject } from '~/common/arrayUtils'
import { customReportsApi } from '~/custom_reports/api'
import { getFormulae } from '~/custom_reports/builder/calculated_fields/Formulae'
import { isCalculatedField, isDatasetField, isParameterField, isSystemVariableField } from '~/custom_reports/shared'
import { CustomFieldPath } from '~/custom_reports/types'
import { lazyMsg } from '~/common/localizationUtils'

const calc_field_option = [{ value: CustomFieldPath.Calculated, label: 'Calculated Fields' }]
const parm_field_option = [{ value: CustomFieldPath.Parameter, label: 'Parameter Fields' }]
const value_field_option = [{ value: CustomFieldPath.Value, label: 'Value' }]
const system_variable_field_option = [{ value: CustomFieldPath.SystemVariable, label: 'System Variables' }]

export const DEFAULT_STATE = {
  query: null,
  datasets: [],
  datasetsObj: {}, // gross need to address later
  fieldSourcesByPath: {},
  calculatedFieldsIndex: {},
  parameterFieldsIndex: {},
  systemVariableFields: [],
  systemVariableFieldsIndex: {},
  joinOptions: null
}

const isNestedObject = obj => typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0

const extractNestedContext = (obj, msg) => {
  if (isNestedObject(obj)) {
    for (const value of Object.values(obj)) {
      if (typeof value === 'object' && value !== null) {
        const context = extractNestedContext(value, msg)
        if (context && context.key) {
          const subLocalized = msg(context.key, context.context || {})
          return { [Object.keys(obj)[0]]: subLocalized }
        }
      }
    }
  }
  return obj
}

export const getLocalizedName = (key, context, msg) => {
  const extratedContext = extractNestedContext(context, msg)
  return msg(key, extratedContext || {})
}

export const useCustomReportBuilderStore = create((set, get) => ({
  ...DEFAULT_STATE,
  actions: {
    setDatasets: datasets =>
      set({
        datasets,
        datasetsObj: arrayToObject(
          datasets,
          ds => ds.id,
          ds => ds
        )
      }),
    setQuery: query => {
      const fieldSourcesByPath = query?.fieldSources
        ? arrayToObject(
            query.fieldSources,
            fs => fs.path,
            fs => fs
          )
        : {}
      const calculatedFieldsIndex = query?.calculatedFields
        ? arrayToObject(
            query.calculatedFields,
            fs => fs.id,
            fs => fs
          )
        : {}
      const parameterFieldsIndex = query?.parameterFields
        ? arrayToObject(
            query.parameterFields,
            fs => fs.id,
            fs => fs
          )
        : {}
      set({ query, fieldSourcesByPath, calculatedFieldsIndex, parameterFieldsIndex })
    },
    setSystemVariableFields: systemVariableFields => {
      const systemVariableFieldsIndex = systemVariableFields
        ? arrayToObject(
            systemVariableFields,
            fs => fs.id,
            fs => fs
          )
        : {}
      set({ systemVariableFields, systemVariableFieldsIndex })
    },
    setJoinOptions: joinOptions => set({ joinOptions }),
    getFields: path => {
      const fieldSourcesByPath = get().fieldSourcesByPath
      const datasetsObj = get().datasetsObj
      if (!fieldSourcesByPath[path] || !datasetsObj[fieldSourcesByPath[path].dataset_id]) {
        // eslint-disable-next-line no-console
        console.log(`Did not find dataset for given path: ${path}`) // debug aid
        return null
      }
      return datasetsObj[fieldSourcesByPath[path].dataset_id].fields
    },
    getField: field => {
      if (!field.path) {
        return null
      }
      const fields = get().actions.getFields(field.path)
      const fieldsIndexedById = arrayToObject(
        fields,
        field => field.id,
        field => field
      )
      return fieldsIndexedById[field.field]
    },
    getDatasetField: params => {
      if (!isDatasetField(params)) {
        return null
      }
      return get().actions.getField(params)
    },
    hasFields: () => {
      const query = get().query
      return !!query.fields?.length
    },
    getDatasetOptions: (datasetFields, calculated, parameters, values, systemVariables) => {
      const query = get().query

      return []
        .concat(
          datasetFields
            ? query.fieldSources.map(fieldSource => ({
                value: fieldSource.path,
                label: fieldSource.display_name
              }))
            : []
        )
        .concat(calculated && query.calculatedFields.length ? calc_field_option : [])
        .concat(parameters && query.parameterFields.length ? parm_field_option : [])
        .concat(values ? value_field_option : [])
        .concat(systemVariables ? system_variable_field_option : [])
    },
    getDatasetFieldOptions: (field, { predicate = null, exclude = [], selectedFields = null } = {}, msg) => {
      if (selectedFields) {
        // add selected fields except current into exclusion list
        selectedFields
          .filter(f => f.field && f.field !== field.field && !f.custom_name)
          .forEach(f => exclude.push(f.field))
      }

      const datasetsObj = get().datasetsObj
      const allFields = datasetsObj[get().actions.getDatasetId(field.path)].fields
      const availableFields = allFields
        .filter(f => !predicate || predicate(f))
        .filter(f => exclude.indexOf(f.id) === -1)
      return availableFields.map(field => ({
        value: field.id,
        label: field.l10n_name_key ? getLocalizedName(field.l10n_name_key, field.l10n_name_context, msg) : field.name
      }))
    },
    getDatasetId: fieldPath => get().fieldSourcesByPath[fieldPath].dataset_id,
    getCalculatedFieldOptions: ({ exclude = [], predicate = null } = {}, msg) => {
      const query = get().query

      if (!query.calculatedFields) return []
      const calculatedFields = predicate ? query.calculatedFields.filter(predicate) : query.calculatedFields
      return calculatedFields
        .filter(field => exclude.indexOf(field.id) === -1)
        .map(field => ({
          value: field.id,
          label: field.l10n_name_key ? getLocalizedName(field.l10n_name_key, field.l10n_name_context, msg) : field.name
        }))
    },
    getParameterFieldOptions: ({ exclude = [], predicate = null } = {}, msg) => {
      const query = get().query

      if (!query.parameterFields) return []
      const parameterFields = predicate ? query.parameterFields.filter(predicate) : query.parameterFields
      return parameterFields
        .filter(field => exclude.indexOf(field.id) === -1)
        .map(field => ({
          value: field.id,
          label: field.l10n_name_key ? getLocalizedName(field.l10n_name_key, field.l10n_name_context, msg) : field.name
        }))
    },
    getSystemVariableFieldOptions: ({ exclude = [], predicate = null } = {}, msg) => {
      const _systemVariableFields = get().systemVariableFields

      if (!_systemVariableFields) return []
      const systemVariableFields = predicate ? _systemVariableFields.filter(predicate) : _systemVariableFields
      return systemVariableFields
        .filter(field => exclude.indexOf(field.id) === -1)
        .map(field => ({
          value: field.id,
          label: field.l10n_name_key ? getLocalizedName(field.l10n_name_key, field.l10n_name_context, msg) : field.name
        }))
    },
    getFieldType: filter => {
      if (isCalculatedField(filter)) {
        const calculatedField = get().calculatedFieldsIndex[filter.field]
        return calculatedField.data_type || getFormulae()[calculatedField.formula].type
      }
      if (isParameterField(filter)) {
        const parameterField = get().parameterFieldsIndex[filter.field]
        return parameterField.data_type
      }
      if (isSystemVariableField(filter)) {
        const systemVariableField = get().systemVariableFieldsIndex[filter.field]
        if (!systemVariableField) return null
        return systemVariableField.data_type
      }
      return get().actions.getField(filter).type
    },
    getFilterType: filter => {
      return isCalculatedField(filter) || isParameterField(filter) || isSystemVariableField(filter)
        ? null
        : get().actions.getField(filter).filter_type
    },
    getFilterMetadata: filter => {
      return isCalculatedField(filter) || isParameterField(filter) || isSystemVariableField(filter)
        ? null
        : get().actions.getField(filter).filter_metadata
    },
    hasParameterFields: () => {
      const query = get().query
      return !!query.parameterFields?.length
    },
    loadRelatedDatasets: async joins => {
      const dataset = get().query.dataset
      const datasets = get().datasets

      const joinOptions = await customReportsApi.getDatasetRelations(dataset, joins)

      get().actions.setJoinOptions(joinOptions)

      const joinOptionsById = arrayToObject(
        joinOptions,
        o => o.id,
        o => o
      )
      const filteredJoins = joins.filter(join => joinOptionsById[join.id])

      const datasetNameById = arrayToObject(
        datasets,
        o => o.id,
        o => lazyMsg(o.l10n_name_key, o.l10n_name_context)
      )
      const fieldSources = [
        {
          id: dataset,
          type: 'dataset',
          dataset_id: dataset,
          path: dataset,
          display_name: datasetNameById[dataset]
        },
        ...filteredJoins.map(join => ({
          id: join.id,
          type: 'join',
          path: join.path,
          target: join.target,
          source: join.source,
          dataset_id: join.target,
          relation_name: join.name,
          display_name: joinOptionsById[join.id].display_name
        }))
      ]
      return { filteredJoins, fieldSources }
    }
  }
}))
